/* NotificationsBell — small bell icon in the top strip.
   Polls /api/notifications/count every 30 s, opens a dropdown with the
   25 most-recent notifications. Click an item to mark it read + navigate. */
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import api from "../lib/api";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "./ui/popover";

const timeAgo = (iso) => {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.max(1, Math.floor(ms / 1000));
  if (s < 60)  return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const KIND_COLOR = {
  "credit.granted": "bg-emerald-500",
  "credit.updated": "bg-blue-500",
  "credit.revoked": "bg-rose-500",
  default:          "bg-foreground/50",
};

export default function NotificationsBell() {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const pollRef = useRef(null);

  const loadCount = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications/count");
      setUnread(data.unread || 0);
    } catch { /* silent */ }
  }, []);

  const loadFeed = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notifications?limit=25");
      setItems(data || []);
    } finally { setLoading(false); }
  }, []);

  // Count polling — every 30 s. Cheap endpoint (single count).
  useEffect(() => {
    loadCount();
    pollRef.current = setInterval(loadCount, 30000);
    return () => pollRef.current && clearInterval(pollRef.current);
  }, [loadCount]);

  // Refresh feed every time popover opens.
  useEffect(() => { if (open) loadFeed(); }, [open, loadFeed]);

  const markRead = async (id) => {
    try {
      await api.patch(`/notifications/${id}/read`);
      setItems(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
      setUnread(prev => Math.max(0, prev - 1));
    } catch { /* silent */ }
  };

  const markAllRead = async () => {
    try {
      await api.post("/notifications/mark-all-read");
      setItems(prev => prev.map(n => ({ ...n, read: true })));
      setUnread(0);
    } catch { /* silent */ }
  };

  const handleClick = async (n) => {
    if (!n.read) await markRead(n.id);
    setOpen(false);
    if (n.link) nav(n.link);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="notifications-bell"
          className="relative w-9 h-9 rounded-full border border-border bg-white hover:border-foreground/40 grid place-items-center transition-colors"
          aria-label="Notifications"
        >
          <Bell className="w-4 h-4 text-foreground/70"/>
          {unread > 0 && (
            <span
              data-testid="notifications-unread-dot"
              className="absolute top-1 right-1 min-w-[16px] h-[16px] px-1 rounded-full bg-rose-500 text-white text-[9px] mono font-semibold grid place-items-center ring-2 ring-white"
            >
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0" data-testid="notifications-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <div>
            <div className="text-sm font-medium">Notifications</div>
            <div className="text-[11px] text-foreground/55">{unread} unread · {items.length} recent</div>
          </div>
          {unread > 0 && (
            <button
              onClick={markAllRead}
              data-testid="notifications-mark-all-read"
              className="text-[11px] text-foreground/60 hover:text-foreground underline-offset-2 hover:underline"
            >
              Mark all read
            </button>
          )}
        </div>
        <div className="max-h-[420px] overflow-y-auto" data-testid="notifications-list">
          {loading && items.length === 0 && (
            <div className="px-4 py-8 text-center text-sm text-foreground/45">Loading…</div>
          )}
          {!loading && items.length === 0 && (
            <div className="px-4 py-10 text-center text-sm text-foreground/50" data-testid="notifications-empty">
              You're all caught up.
            </div>
          )}
          {items.map(n => {
            const dotColor = KIND_COLOR[n.kind] || KIND_COLOR.default;
            return (
              <button
                key={n.id}
                onClick={() => handleClick(n)}
                data-testid={`notification-${n.id}`}
                className={`w-full text-left px-4 py-3 border-b border-border/60 hover:bg-secondary/30 transition-colors flex gap-3 ${n.read ? "opacity-70" : ""}`}
              >
                <div className="relative mt-1 shrink-0">
                  <div className={`w-2 h-2 rounded-full ${dotColor}`}/>
                  {!n.read && (
                    <div className={`absolute inset-0 w-2 h-2 rounded-full ${dotColor} animate-ping opacity-60`}/>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className={`text-[13px] ${n.read ? "" : "font-medium"} truncate`}>{n.title}</div>
                  {n.body && (
                    <div className="text-[11px] text-foreground/60 mt-0.5 line-clamp-2">{n.body}</div>
                  )}
                  <div className="text-[10px] text-foreground/40 mt-1 mono uppercase tracking-wider">
                    {n.kind.replace(/\./g, " · ")} · {timeAgo(n.created_at)}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
